
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { HiOutlineExclamationTriangle } from "react-icons/hi2";
import { toast } from "react-toastify";
import Badge from '../../components/Badge';
import Button from "../../components/Button";
import FilterPanel from "../../components/FilterPanel";
import PageHeader from "../../components/PageHeader";
import Select from "../../components/Select";
import Table from "../../components/Table";
import { api } from "../../services/api";

export default function AdminEmails() {

  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: "",
    type: "",
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadEmails();
  }, [filters, page]);

  const loadEmails = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append("status", filters.status);
      if (filters.type) params.append("type", filters.type);
      params.append("page", page);
      params.append("limit", 20); // You can adjust the page size here

      const response = await api.get(`/admin/emails?${params.toString()}`);
      setEmails(response.emails || []);
      if (response.pagination) {
        setTotalPages(response.pagination.pages || 1);
      } else {
        setTotalPages(1);
      }
    } catch (error) {
      toast.error("Failed to load emails");
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (emailId) => {
    try {
      await api.post(`/admin/emails/${emailId}/retry`);
      toast.success("Email retry queued");
    } catch (error) {
      toast.error(error.error || "Retry failed");
    }
  };

  const statusVariantMap = {
    sent: "success",
    failed: "error",
    pending: "warning",
  };

  const columns = [
    {
      header: "To",
      accessor: "toEmail",
      sortable: true,
      render: (email) => (
        <div>
          <p className="text-sm font-medium text-text">{email.toEmail}</p>
          {email.toUserId?.name && (
            <p className="text-xs text-text opacity-60">
              {email.toUserId.name.first} {email.toUserId.name.last}
            </p>
          )}
        </div>
      ),
    },
    {
      header: "Type",
      accessor: "type",
      sortable: true,
      render: (email) => (
        <span className="opacity-70">{email.type.replace("_", " ")}</span>
      ),
    },
    {
      header: "Status",
      accessor: "status",
      sortable: true,
      render: (email) => (
        <Badge
          label={email.status?.replace(/_/g, " ")}
          variant={statusVariantMap[email.status] || "default"}
          size="small"
          className="capitalize"
        />
      ),
    },
    {
      header: "Sent At",
      accessor: "sentAt",
      sortable: true,
      render: (email) => (
        <span className="opacity-70">
          {dayjs(email.sentAt).format("MMM D, YYYY h:mm A")}
        </span>
      ),
    },
    {
      header: "Actions",
      sortable: false,
      render: (email) => (
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm">
            <a
              href={`/admin/emails/${email._id}`}
              className="text-primary hover:opacity-80"
            >
              View
            </a>
          </Button>
          {email.status === "failed" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleRetry(email._id)}
            >
              Retry
            </Button>
          )}
          {email.errorMessage && (
            <span
              className="text-xs text-accent flex items-center gap-1"
              title={email.errorMessage}
            >
              <HiOutlineExclamationTriangle className="flex-shrink-0" /> Error
            </span>
          )}
        </div>
      ),
    },
  ];

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "sent", label: "Sent" },
    { value: "failed", label: "Failed" },
    { value: "pending", label: "Pending" },
  ];

  const typeOptions = [
    { value: "", label: "All Types" },
    { value: "email_verification", label: "Email Verification" },
    { value: "booking_request", label: "Booking Request" },
    { value: "booking_confirmed", label: "Booking Confirmed" },
    { value: "session_reminder", label: "Session Reminder" },
    { value: "no_show", label: "No Show" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader title="Email Logs" />

      <FilterPanel>
        <Select
          label="Status"
          value={filters.status}
          onChange={(e) => {
            setFilters({ ...filters, status: e.target.value });
            setPage(1);
          }}
          options={statusOptions}
        />
        <Select
          label="Type"
          value={filters.type}
          onChange={(e) => {
            setFilters({ ...filters, type: e.target.value });
            setPage(1);
          }}
          options={typeOptions}
        />
      </FilterPanel>

      <Table
        columns={columns}
        data={emails.map((email) => ({ ...email, id: email._id }))}
        loading={loading}
        emptyMessage="No emails found"
      />

      {/* Pagination Controls */}
      <div className="flex justify-center items-center gap-2 mt-6">
        <Button
          variant="secondary"
          size="sm"
          disabled={page === 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          Previous
        </Button>
        <span className="text-sm opacity-70">
          Page {page} of {totalPages}
        </span>
        <Button
          variant="secondary"
          size="sm"
          disabled={page === totalPages}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
